import { toSecondaryQuantity } from "@heartinz/finance";

/**
 * Global handleChange utility — supports three variants:
 *
 * Variant A (ProductEdit - basic):
 *   - uses `isProductData` as the base state key
 *   - has `quantity` field with uRLs.UNIT flag (3 decimal places)
 *   - note popup uses `noteOpenStatus` callback
 *   - has `code` field (no whitespace)
 *
 * Variant B (ProductAdd - simpler form):
 *   - uses `productData` as the base state key
 *   - no quantity / uRLs logic
 *   - note popup has no `noteOpenStatus` callback
 *   - clears all errors first via `setErrors("")`
 *
 * Variant C (ProductEdit - advanced):
 *   - uses `isProductData` as the base state key
 *   - `quantity` supports `isSecondaryUnit()` check (1 decimal) vs primary (3 decimals)
 *   - `lcmultiplayer` field: finance calculations, early return, localStorage save
 *   - `landingcost` field: clears lcmultiplayer, early return
 *   - `quantity` field: updates `setApiData` separately, no recalculate
 *   - price regex allows empty string: /^$|^\d*\.?\d{0,2}$/
 *   - clears all errors first via `setErrors("")`
 *
 * @param {React.ChangeEvent} e - Input change event
 * @param {object} params
 *
 * ─ Shared ────────────────────────────────────────────────────────────────────
 * @param {object}   params.isProductData               - Base state (Variant A & C)
 * @param {object}   params.productData                 - Base state (Variant B)
 * @param {object}   params.noteSuggest                 - { category: [items] }
 * @param {Array}    params.brandSuggest                - [{ title }]
 * @param {Function} params.setProductData
 * @param {Function} params.setErrors
 * @param {Function} params.setFilteredNoteSuggestions
 * @param {Function} params.setShowNotePopup
 * @param {Function} params.setHighlightedIndex
 * @param {Function} params.setFilteredBrand
 * @param {Function} params.setBrandIndex
 * @param {Function} params.setIsBrandFocused
 * @param {Function} params.handleCalculate
 * @param {Function} params.validateField                - (name, value) => error | null
 *
 * ─ Variant A only ─────────────────────────────────────────────────────────
 * @param {object}   params.uRLs                        - Config flags (uRLs.UNIT)
 * @param {Function} params.noteOpenStatus              - Note popup status callback
 *
 * ─ Variant B only ─────────────────────────────────────────────────────────
 * @param {boolean}  params.clearErrorsOnChange         - true → setErrors("") on each change
 *
 * ─ Variant C only ─────────────────────────────────────────────────────────
 * @param {object}   params.uRLs                        - Config flags (uRLs.UNIT)
 * @param {object}   params.unitData                    - Unit data object
 * @param {Function} params.isSecondaryUnit             - () => boolean
 * @param {object}   params.finance                     - Finance util { getFinalLandingCost, toSubunit, toCurrency }
 * @param {Function} params.setApiData                  - Setter for API data (quantity sync)
 * @param {Function} params.noteOpenStatus              - Note popup status callback
 *
 * ─ Variant D only ─────────────────────────────────────────────────────────
 * @param {boolean}  params.isVariantD                  - Pass `true` to activate Variant D mode
 * @param {object}   params.uRLs                        - Config flags (uRLs.UNIT)
 * NOTE: Variant D reads isSecondaryUnit from isProductData.isSecondaryUnit (boolean)
 *       not from a function. It uses `stock` field instead of `quantity`.
 *       Price fields use permissive regex (allows empty + mid-type dot).
 *       No note/brand/suggestions logic. No validateField call.
 */
export const handleChange = (e, params) => {
  const {
    // state — accept either key name
    productData,
    isProductData,

    // flags
    uRLs,
    unitData,
    clearErrorsOnChange = false,

    // suggestions
    noteSuggest,
    brandSuggest,

    // setters
    setProductData,
    setErrors,
    setFilteredNoteSuggestions,
    setShowNotePopup,
    setHighlightedIndex,
    setFilteredBrand,
    setBrandIndex,
    setIsBrandFocused,

    // callbacks
    handleCalculate,
    validateField,
    noteOpenStatus,     // optional — Variant A & C
    isSecondaryUnit,    // optional — Variant C only
    finance,            // optional — Variant C only
    setApiData,         // optional — Variant C only
    isVariantD,         // optional — Variant D only
  } = params;

  // Variant B & C: wipe all errors on every keystroke
  if (clearErrorsOnChange) {
    setErrors("");
  }

  const { name, value } = e.target;
  let inputValue = value;

  // resolve base state — Variant A & C use isProductData, Variant B uses productData
  const baseData = isProductData ?? productData;

  // ── code: no whitespace allowed (Variant A) ───────────────────────────────
  if (name === "code") {
    if (/\s/.test(value)) inputValue = "";
  }

  // ── hsnsac: up to 8 digits only ──────────────────────────────────────────
  if (name === "hsnsac") {
    if (value !== "" && !/^\d{0,8}$/.test(value)) return;
    inputValue = value;
  }

  // ── price fields: digits only, up to 2 decimal places (all variants) ───────
  // allows "" so user can fully clear the field, blocks everything else invalid
  if (["mrp", "saleprice", "baseprice", "landingcost"].includes(name)) {
    if (value !== "" && !/^\d+(\.\d{0,2})?$/.test(value)) return;
  }

  // ── taxpercentage: must be a valid GST slab ───────────────────────────────
  if (name === "taxpercentage") {
    const allowedValues = [0, 5, 12, 18, 24];
    if (!allowedValues.includes(Number(value))) inputValue = 0;
  }

  // ── note: filter suggestions & manage popup ───────────────────────────────
  if (name === "note") {
    if (value.trim() === "") {
      setFilteredNoteSuggestions(noteSuggest);
      setShowNotePopup(false);
      noteOpenStatus?.(false);
    } else {
      const filtered = {};
      Object.entries(noteSuggest).forEach(([category, items]) => {
        const matched = items.filter((item) =>
          item.toLowerCase().includes(value.toLowerCase())
        );
        if (matched.length > 0) filtered[category] = matched;
      });

      const hasResults = Object.keys(filtered).length > 0;
      setFilteredNoteSuggestions(filtered);
      setShowNotePopup(hasResults);
      noteOpenStatus?.(hasResults);
    }
    setHighlightedIndex(-1);
  }

  // ── brandName: filter brand list & keep dropdown open ────────────────────
  if (name === "brandName") {
    const baseList = brandSuggest || [];
    const filtered =
      value.trim() === ""
        ? baseList
        : baseList.filter((item) =>
            item?.title?.toLowerCase().includes(value.toLowerCase())
          );

    setFilteredBrand(filtered);
    setBrandIndex(-1);
    setIsBrandFocused(true);
  }

  // ── quantity: validation (before early-return blocks below) ──────────────
  if (name === "quantity" && uRLs) {
    if (uRLs.UNIT === "true" && unitData) {
      // Variant C — isSecondaryUnit check
      if (isSecondaryUnit?.()) {
        if (!/^\d*\.?\d{0,1}$/.test(value)) return; // 1 decimal for secondary
      } else {
        if (!/^\d*\.?\d{0,3}$/.test(value)) return; // 3 decimals for primary
      }
    } else {
      if (!/^\d*$/.test(value)) return; // integers only
    }
  }

  // ── lcmultiplayer: finance calc, saves to localStorage, early return ──────
  // Variant C only
  if (name === "lcmultiplayer" && finance) {
    const regex = /^$|^\d*\.?\d{0,10}$/;
    if (!regex.test(value)) return;

    const multiplier = parseFloat(value) || 0;
    const landing = finance.getFinalLandingCost(
      multiplier,
      finance.toSubunit(baseData.finalLandingCost, "INR")
    );

    const updatedData = {
      ...baseData,
      lcmultiplayer: value,
      landingcost: finance.toCurrency(landing, "INR"),
    };

    localStorage.setItem("lastMultiplier", value);
    setProductData(updatedData);
    handleCalculate(updatedData);
    return; // ← early return, skip generic commit below
  }

  // ── landingcost: clears lcmultiplayer, early return ───────────────────────
  // Variant C only
  if (name === "landingcost" && finance) {
    const updatedData = {
      ...baseData,
      landingcost: inputValue,
      lcmultiplayer: "",
    };

    setProductData(updatedData);
    handleCalculate(updatedData);
    return; // ← early return, skip generic commit below
  }

  // ── quantity: separate API data sync, no recalculate, early return ────────
  // Variant C only
  if (name === "quantity" && setApiData) {
    const updatedData = { ...baseData, quantity: inputValue };

    setProductData(updatedData);
    setApiData((prev) => ({
      ...prev,
      quantity: Number(inputValue) || 0,
    }));
    return; // ← early return, no handleCalculate
  }

  // ── stock field: always active when name === "stock" ────────────────────────
  // reads isSecondaryUnit as boolean from baseData (not a function)
  if (name === "stock") {
    if (uRLs?.UNIT === "true" && (baseData?.stockLeftUnit || baseData?.unitConfig)) {
      // unit enabled — allow decimals based on unit type
      if (baseData?.isSecondaryUnit) {
        if (!/^\d*\.?\d{0,1}$/.test(value)) return; // secondary → 1 decimal
      } else {
        if (!/^\d*\.?\d{0,3}$/.test(value)) return; // primary → 3 decimals
      }
    } else {
      // no unit OR uRLs.UNIT !== "true" → always integers only
      // this also covers the first-render case before editData loads
      if (!/^\d*$/.test(value)) return;
    }
  }

  
  // ── generic commit: update state & recalculate ────────────────────────────
  const updatedData = { ...baseData, [name]: inputValue };
  setProductData(updatedData);
  handleCalculate(updatedData);

  setErrors((prevErrors) => ({
    ...prevErrors,
    [name]: validateField(name, inputValue),
  }));
};


export const secondaryQuanity = (quanity, unitData) => {
  if (
    !unitData ||
    !unitData.secondaryUnitCode ||
    !unitData.primaryUnitCode ||
    !unitData.conversionFactor // adjust to whatever field toSecondaryQuantity actually requires
  ) {
    return quanity ?? 0; // fallback: show primary value rather than crashing
  }
  try {
    const qty = toSecondaryQuantity(quanity, unitData);
    return qty?.secondaryQty ?? quanity ?? 0;
  } catch (err) {
    console.error("toSecondaryQuantity failed for unitData:", unitData, err);
    return quanity ?? 0;
  }
};